import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

/**
 * GPS Context - Unified GPS State Management
 * 
 * Modes:
 * - OFF: GPS disabled, no tracking
 * - LOCATE: GPS active, one-time center on user location (no auto-pan)
 * - RADAR: GPS tracking active, updates location AND map auto-pans to follow user
 */

const GpsContext = createContext();

export const GPS_MODES = {
  OFF: 'OFF',
  LOCATE: 'LOCATE',
  RADAR: 'RADAR'
};

export const GpsProvider = ({ children }) => {
  const [gpsMode, setGpsMode] = useState(GPS_MODES.OFF);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [lastLocation, setLastLocation] = useState(() => {
    // Load last map center location from localStorage
    const saved = localStorage.getItem('last_map_location');
    return saved ? JSON.parse(saved) : null;
  });
  const [lastError, setLastError] = useState(null);
  const gpsWatchIdRef = useRef(null);

  // Start GPS tracking
  const startGps = useCallback((mode) => {
    console.log(`🎯 Starting GPS in ${mode} mode`);
    
    // Stop any existing tracking first
    if (gpsWatchIdRef.current) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }

    // Validate mode
    if (!Object.values(GPS_MODES).includes(mode)) {
      console.error(`Invalid GPS mode: ${mode}`);
      return;
    }

    // Check if geolocation is available
    if (!navigator.geolocation) {
      const error = 'Geolocation is not supported by this browser';
      console.error(error);
      setLastError(error);
      return;
    }

    // Start watching position
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        
        setCurrentLocation(newLocation);
        setLastError(null);
      },
      (error) => {
        let errorMessage = 'ไม่สามารถเข้าถึง GPS ได้';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'คุณไม่ได้อนุญาตให้เข้าถึงตำแหน่ง กรุณาอนุญาตใน browser settings';
            setLastError(errorMessage);
            alert(`GPS Error: ${errorMessage}`);
            // Only PERMISSION_DENIED should kill GPS entirely
            setGpsMode(GPS_MODES.OFF);
            if (gpsWatchIdRef.current) {
              navigator.geolocation.clearWatch(gpsWatchIdRef.current);
              gpsWatchIdRef.current = null;
            }
            break;
          case error.POSITION_UNAVAILABLE:
          case error.TIMEOUT:
            // Transient errors — keep GPS running, don't stop
            console.warn(`GPS transient error [${error.code}]: ${error.message}`);
            setLastError(error.code === error.TIMEOUT ? 'กำลังค้นหาตำแหน่ง...' : 'GPS ไม่พร้อมใช้งาน รอสักครู่...');
            break;
          default:
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 10000
      }
    );

    gpsWatchIdRef.current = watchId;
    setGpsMode(mode);
  }, []);

  // Stop GPS tracking
  const stopGps = useCallback(() => {
    console.log('🛑 Stopping GPS');
    
    if (gpsWatchIdRef.current) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
    
    setGpsMode(GPS_MODES.OFF);
    setLastError(null);
  }, []);

  // Set GPS mode directly (used for LOCATE→RADAR transitions and manual downgrades)
  const setMode = useCallback((mode) => {
    if (mode === GPS_MODES.OFF) {
      stopGps();
    } else if (gpsMode === GPS_MODES.OFF) {
      // Need to start GPS hardware first
      startGps(mode);
    } else {
      // GPS already running, just change mode
      setGpsMode(mode);
    }
  }, [gpsMode, startGps, stopGps]);

  // Toggle GPS mode: OFF -> LOCATE -> RADAR -> OFF
  const cycleGpsMode = useCallback(() => {
    switch (gpsMode) {
      case GPS_MODES.OFF:
        startGps(GPS_MODES.LOCATE);
        break;
      case GPS_MODES.LOCATE:
        setGpsMode(GPS_MODES.RADAR);
        break;
      case GPS_MODES.RADAR:
        stopGps();
        break;
      default:
        stopGps();
    }
  }, [gpsMode, startGps, stopGps]);

  // Update last location (last map center - updated always when map moves)
  const updateLastLocation = useCallback((location) => {
    if (!location || !location.lat || !location.lng) {
      console.warn('Invalid location provided to updateLastLocation');
      return;
    }
    
    const lastLoc = {
      lat: location.lat,
      lng: location.lng,
      timestamp: Date.now()
    };
    
    console.log('📍 Last location updated:', lastLoc);
    setLastLocation(lastLoc);
    localStorage.setItem('last_map_location', JSON.stringify(lastLoc));
  }, []);

  // Get GPS status info
  const getGpsStatus = useCallback(() => {
    return {
      mode: gpsMode,
      isActive: gpsMode !== GPS_MODES.OFF,
      isLocate: gpsMode === GPS_MODES.LOCATE,
      isTracking: gpsMode === GPS_MODES.RADAR,
      isRadar: gpsMode === GPS_MODES.RADAR,
      hasLocation: currentLocation !== null,
      hasLastLocation: lastLocation !== null,
      lastError: lastError
    };
  }, [gpsMode, currentLocation, lastLocation, lastError]);

  // Screen Wake Lock — keep screen on during RADAR mode
  const wakeLockRef = useRef(null);

  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener('release', () => {
        wakeLockRef.current = null;
      });
    } catch (e) {
      // Low battery or other OS restriction — silently ignore
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch {}
      wakeLockRef.current = null;
    }
  }, []);

  // Acquire/release wake lock when GPS mode changes
  useEffect(() => {
    if (gpsMode === GPS_MODES.RADAR) {
      acquireWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [gpsMode, acquireWakeLock, releaseWakeLock]);

  // Re-acquire wake lock when tab becomes visible again (OS releases it on tab switch)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && gpsMode === GPS_MODES.RADAR) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [gpsMode, acquireWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gpsWatchIdRef.current) {
        navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
      }
    };
  }, []);

  const value = {
    // State
    gpsMode,
    currentLocation,
    lastLocation,
    lastError,
    
    // Actions
    startGps,
    stopGps,
    cycleGpsMode,
    setMode,
    updateLastLocation,
    
    // Utils
    getGpsStatus,
    GPS_MODES
  };

  return (
    <GpsContext.Provider value={value}>
      {children}
    </GpsContext.Provider>
  );
};

// Custom hook to use GPS context
export const useGps = () => {
  const context = useContext(GpsContext);
  if (!context) {
    throw new Error('useGps must be used within a GpsProvider');
  }
  return context;
};

export default GpsContext;
